import React, { useEffect, useRef } from "react";

interface OceShaActionSectionProps {
  youtubeUrl?: string;
}

const DEFAULT_YOUTUBE_URL =
  "https://www.youtube.com/embed/crbfiYw3WQE?autoplay=1&mute=1&loop=1&playlist=crbfiYw3WQE&rel=0&playsinline=1&enablejsapi=1";

const OceShaActionSection: React.FC<OceShaActionSectionProps> = ({
  youtubeUrl = DEFAULT_YOUTUBE_URL,
}) => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const sendYouTubeCommand = (
    command: "playVideo" | "pauseVideo"
  ) => {
    if (!iframeRef.current?.contentWindow) return;

    iframeRef.current.contentWindow.postMessage(
      JSON.stringify({
        event: "command",
        func: command,
        args: [],
      }),
      "*"
    );
  };

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
          sendYouTubeCommand("playVideo");
        } else {
          sendYouTubeCommand("pauseVideo");
        }
      },
      {
        threshold: [0, 0.35, 0.6, 1],
      }
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handlePlay = () => {
    sendYouTubeCommand("playVideo");
  };

  return (
    <div
      ref={sectionRef}
      className="ocesha-action-wrapper"
    >
      <div className="ocesha-action-card">

        {/* ================================
            LEFT
        ================================= */}

        <div className="ocesha-action-content">

          <div className="ocesha-action-brand">
            <span className="ocesha-action-brand-icon">
              C
            </span>

            <span>OceSha.ai</span>
          </div>

          <h2 className="ocesha-action-title">
            See OceSha
            <span>
              in <strong>Action</strong>
            </span>
          </h2>

          <div className="ocesha-action-line" />

          <h3 className="ocesha-action-tagline">
            Create. Teach. Publish. Grow.
          </h3>

          <p className="ocesha-action-description">
            The all-in-one AI platform to
            <br />
            create, teach, and grow your
            <br />
            course business.
          </p>

          <button
            type="button"
            className="ocesha-action-cta"
          >
            <span className="ocesha-action-sparkles">
              ✦
            </span>

            <span>
              AI Creates. AI Teaches.
              <br />
              You Grow.
            </span>
          </button>

        </div>

        {/* ================================
            RIGHT VIDEO
        ================================= */}

        <div className="ocesha-action-video-area">

          <div className="ocesha-action-video-container">

            <iframe
              ref={iframeRef}
              src={youtubeUrl}
              title="OceSha AI product demonstration"
              className="ocesha-action-video"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />

          </div>

          <button
            type="button"
            className="ocesha-action-play"
            onClick={handlePlay}
            aria-label="Play OceSha demonstration"
          >
            <span />
          </button>

        </div>
      </div>

      <style>{`

        /* ============================================
           OUTER
        ============================================ */

        .ocesha-action-wrapper,
        .ocesha-action-wrapper * {
          box-sizing: border-box;
        }

        .ocesha-action-wrapper {
          width: 80%;
          max-width: 1600px;
          margin: 0 auto
        }


        /* ============================================
           CARD
        ============================================ */

        .ocesha-action-card {
          width: 100%;

          min-height: 560px;

          display: grid;

          grid-template-columns:
            45%
            55%;

          position: relative;

          overflow: hidden;

          border-radius: 24px;

          border: 1px solid #e3ecfb;

          background:
            radial-gradient(
              ellipse 55% 70% at 0% 0%,
              rgba(142,113,255,.18) 0%,
              rgba(255,255,255,0) 72%
            ),
            radial-gradient(
              ellipse 55% 70% at 100% 0%,
              rgba(142,113,255,.15) 0%,
              rgba(255,255,255,0) 72%
            ),
            linear-gradient(
              180deg,
              #ffffff 0%,
              #fcfbff 100%
            );

          box-shadow:
            0 24px 60px
            rgba(70,110,220,.12);
        }


        /* ============================================
           LEFT CONTENT
        ============================================ */

        .ocesha-action-content {
          min-width: 0;

          display: flex;

          flex-direction: column;

          justify-content: center;

          padding:
            48px
            30px
            48px
            50px;

          position: relative;

          z-index: 2;
        }


        .ocesha-action-brand {
          display: flex;

          align-items: center;

          gap: 9px;

          margin-bottom: 42px;

          font-size: 25px;

          font-weight: 800;

          color: #071044;
        }


        .ocesha-action-brand-icon {
          width: 34px;
          height: 34px;

          display: flex;

          align-items: center;
          justify-content: center;

          border:
            4px solid #2050e8;

          border-right-color:
            transparent;

          border-radius: 50%;

          color: #5b32f4;

          font-size: 18px;

          font-weight: 900;
        }


        .ocesha-action-title {
          margin: 0;

          font-size:
            clamp(
              48px,
              5.5vw,
              78px
            );

          line-height: .95;

          letter-spacing:
            -3.5px;

          font-weight: 800;

          color: #071044;
        }


        .ocesha-action-title span {
          display: block;

          margin-top: 7px;
        }


        .ocesha-action-title strong {
          color: #5130f3;
        }


        .ocesha-action-line {
          width: 78px;
          height: 3px;

          margin:
            26px
            0
            22px;

          background: #5735ef;

          border-radius: 10px;
        }


        .ocesha-action-tagline {
          margin:
            0
            0
            10px;

          font-size:
            clamp(
              19px,
              2vw,
              27px
            );

          line-height: 1.2;

          font-weight: 700;

          color: #071044;
        }


        .ocesha-action-description {
          margin: 0;

          font-size:
            clamp(
              15px,
              1.4vw,
              20px
            );

          line-height: 1.45;

          color: #26325d;

          font-weight: 500;
        }


        /* ============================================
           CTA
        ============================================ */

        .ocesha-action-cta {
          width: fit-content;

          display: flex;

          align-items: center;

          gap: 12px;

          margin-top: 28px;

          padding:
            14px
            19px;

          border: 0;

          border-radius: 12px;

          background:
            linear-gradient(
              105deg,
              #3820e6,
              #642ff8,
              #4320e7
            );

          color: white;

          font-family: inherit;

          font-size: 17px;

          line-height: 1.2;

          font-weight: 700;

          text-align: left;

          box-shadow:
            0 12px 25px
            rgba(
              65,
              35,
              216,
              .25
            );
        }


        .ocesha-action-sparkles {
          font-size: 27px;
        }


        /* ============================================
           VIDEO AREA
        ============================================ */

        .ocesha-action-video-area {
          min-width: 0;

          position: relative;

          display: flex;

          align-items: center;

          justify-content: center;

          padding:
            40px
            35px
            40px
            10px;

          overflow: visible;
        }


        .ocesha-action-video-container {
          position: relative;

          width: 100%;

          max-width: 720px;

          aspect-ratio: 16 / 9;

          overflow: hidden;

          border-radius: 18px;

          background: #101324;

          box-shadow:
            0 20px 45px
            rgba(
              31,
              27,
              85,
              .20
            );
        }


        .ocesha-action-video {
          width: 100%;
          height: 100%;

          display: block;

          border: 0;
        }


        /* ============================================
           PLAY BUTTON
        ============================================ */

        .ocesha-action-play {
          position: absolute;

          z-index: 10;

          left: 1px;

          top: 50%;

          width:
            clamp(
              94px,
              9vw,
              130px
            );

          aspect-ratio: 1;

          transform:
            translate(
              -50%,
              -50%
            );

          display: flex;

          align-items: center;
          justify-content: center;

          border:
            6px solid
            rgba(
              255,
              255,
              255,
              .92
            );

          border-radius: 50%;

          background:
            linear-gradient(
              145deg,
              #3c22ee,
              #5225f5
            );

          box-shadow:
            0 15px 35px
            rgba(
              55,
              28,
              226,
              .35
            ),
            0 0 0 5px
            rgba(
              255,
              255,
              255,
              .35
            );

          cursor: pointer;
        }


        .ocesha-action-play span {
          width: 0;
          height: 0;

          margin-left: 7%;

          border-top:
            20px solid transparent;

          border-bottom:
            20px solid transparent;

          border-left:
            30px solid white;
        }


        /* ============================================
           TABLET
        ============================================ */

        @media (max-width: 1000px) {

          .ocesha-action-card {
            min-height: 500px;
          }

          .ocesha-action-content {
            padding:
              38px
              20px
              38px
              35px;
          }

          .ocesha-action-title {
            font-size: 55px;
            letter-spacing: -3px;
          }

          .ocesha-action-video-area {
            padding:
              30px
              25px
              30px
              5px;
          }
        }


        /* ============================================
           MOBILE
        ============================================ */

        @media (max-width: 767px) {

          .ocesha-action-card {
            display: flex;

            flex-direction: column;

            min-height: auto;

            border-radius: 24px;
          }


          .ocesha-action-content {
            width: 100%;

            padding:
              34px
              22px
              28px;

            align-items: center;

            text-align: center;
          }


          .ocesha-action-brand {
            margin-bottom: 28px;
          }


          .ocesha-action-title {
            font-size:
              clamp(
                44px,
                13vw,
                62px
              );

            letter-spacing:
              -2.5px;
          }


          .ocesha-action-line {
            margin:
              21px
              auto
              18px;
          }


          .ocesha-action-tagline {
            font-size: 21px;
          }


          .ocesha-action-description {
            font-size: 15px;
          }


          .ocesha-action-cta {
            margin-top: 23px;

            font-size: 15px;
          }


          .ocesha-action-video-area {
            width: 100%;

            padding:
              5px
              18px
              30px;

            overflow: visible;
          }


          .ocesha-action-video-container {
            width: 100%;

            max-width: none;

            border-radius: 16px;
          }


          .ocesha-action-play {
            left: 8px;

            width: 80px;

            border-width: 5px;
          }


          .ocesha-action-play span {
            border-top-width: 14px;
            border-bottom-width: 14px;
            border-left-width: 22px;
          }
        }

      `}</style>
    </div>
  );
};

export default OceShaActionSection;